'use client';

import { useEffect, useMemo, useState } from 'react';

import { useLazyQuery, useQuery } from '@apollo/client/react';
import { css } from '@emotion/react';
import { ExpandMore } from '@mui/icons-material';
import {
  Typography,
  Box,
  Divider,
  Container,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Skeleton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { EventHero, Image } from '@scorenco/components';
import { Types, useEventLive, useTranslation, Locale } from '@scorenco/core';
import { format, startOfToday, subDays } from 'date-fns';
import { fr, de } from 'date-fns/locale';
import { useRouter } from 'next/router';

import { T_KEYS } from '@/translations';

import { LiveWidgetTitle } from './_internal/LiveWidgetTitle';
import {
  WidgetLiveGetTeamsSponsorsDocument,
  WidgetGetEventDataDocument,
  WidgetGetEventIdWithDateFilterDocument,
  WidgetGetReportersDocument,
} from './_internal/Widget.query.generated';
import { WidgetLivePosts } from './_internal/WidgetLivePosts';

const backgroundImagePath = (event: Types.Competitions_Event_Detail) => {
  if (!event) {
    return undefined;
  }

  const category = ['SENIOR', 'U2', 'LOISIR', 'VETERAN'].find((category) =>
    category.startsWith(event.age_category ?? '')
  )
    ? 'seniors'
    : 'juniors';
  const gender = event.gender === 'F' ? 'women' : 'men';

  return `/images/event-hero/${event?.sport?.slug}-${gender}-${category}.webp`;
};

type EventTeam = {
  team_id?: number;
};

const getNumberFromUnknown = (value: unknown) =>
  typeof value === 'number' ? value : undefined;

const getDateInput = (value: unknown): string | number | Date | undefined => {
  if (typeof value === 'string' || typeof value === 'number') {
    return value;
  }

  return value instanceof Date ? value : undefined;
};

const getEventTeams = (value: unknown): EventTeam[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map((item) => {
    if (typeof item !== 'object' || item === null) {
      return {};
    }

    const team = item as Record<string, unknown>;
    return {
      team_id: getNumberFromUnknown(team.team_id),
    };
  });
};

const SponsorCarousel = ({
  sponsors,
}: {
  sponsors: ReadonlyArray<{ sponsor?: { logo?: string | null } | null }>;
}) => {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (sponsors.length <= 1) return undefined;

    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % sponsors.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [sponsors.length]);

  return (
    <Box sx={{ position: 'relative', height: '7rem' }}>
      {sponsors.map((sponsor, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: i === activeIndex ? 1 : 0,
            transition: 'opacity 0.5s ease-in-out',
          }}
        >
          <Image
            src={sponsor.sponsor?.logo ?? undefined}
            css={() => css`
              height: 7rem;
              width: auto;
            `}
            width={120}
            height={120}
          />
        </Box>
      ))}
    </Box>
  );
};

interface LiveWidgetProps {
  widgetTeamId?: number;
  liveSettings?: {
    nb_days_update?: unknown;
    live_id?: number;
  } | null;
  clubId?: number | string;
}

export const LiveWidget = ({
  widgetTeamId,
  liveSettings,
  clubId,
}: LiveWidgetProps) => {
  const { t, locale } = useTranslation();
  const theme = useTheme();
  const router = useRouter();

  const [lastEvent, lastEventData] = useLazyQuery(
    WidgetGetEventIdWithDateFilterDocument
  );

  const [nextEvent, nextEventData] = useLazyQuery(
    WidgetGetEventIdWithDateFilterDocument
  );

  const getWidgetReporters = useQuery(WidgetGetReportersDocument, {
    variables: {
      clubId: Number(clubId),
      cacheTTL: router.query.noCache ? 0 : undefined,
    },
    skip: !clubId,
    context: {
      role: 'anonymous',
    },
  });

  const widgetReporters = getWidgetReporters.data?.members_member;
  const nbDaysUpdate = getNumberFromUnknown(liveSettings?.nb_days_update);

  // Derive eventId from liveSettings and query results
  const eventId = useMemo(() => {
    if (!liveSettings) return undefined;
    if (liveSettings.live_id) return liveSettings.live_id;
    if (!nbDaysUpdate) return null;

    // Check last event data first
    if (lastEventData.called && !lastEventData.loading) {
      const list = lastEventData.data?.competitions_event_detail_by_team_id;
      if (list?.[0]) {
        const _event = list[0];
        const eventDate = getDateInput(_event.date);
        if (!eventDate) {
          return null;
        }
        const isWithinRange =
          new Date(eventDate).getTime() >
          new Date(subDays(startOfToday(), nbDaysUpdate)).getTime();
        if (isWithinRange) return _event.id;
      }
    }

    // Fall back to next event data
    if (nextEventData.data?.competitions_event_detail_by_team_id?.[0]?.id) {
      return nextEventData.data.competitions_event_detail_by_team_id[0].id;
    }

    // Fall back to last event data
    if (lastEventData.data?.competitions_event_detail_by_team_id?.[0]?.id) {
      return lastEventData.data.competitions_event_detail_by_team_id[0].id;
    }

    // Both queries resolved with no data
    if (lastEventData.called && nextEventData.called) return null;

    return undefined;
  }, [liveSettings, nbDaysUpdate, lastEventData, nextEventData]);

  // Trigger lazy queries based on liveSettings and query results
  useEffect(() => {
    if (!nbDaysUpdate || liveSettings?.live_id) return;
    if (!lastEventData.called) {
      lastEvent({
        variables: { dateFilter: -1, teamId: widgetTeamId! },
        context: { role: 'anonymous' },
      });
    }
  }, [nbDaysUpdate, liveSettings?.live_id, lastEventData.called, lastEvent]);

  useEffect(() => {
    if (!nbDaysUpdate || liveSettings?.live_id) return;
    if (!lastEventData.called || lastEventData.loading) return;

    const list = lastEventData.data?.competitions_event_detail_by_team_id;
    const lastEventDate = getDateInput(list?.[0]?.date);
    const needsNext =
      !list?.[0] ||
      !lastEventDate ||
      new Date(lastEventDate).getTime() <=
        new Date(subDays(startOfToday(), nbDaysUpdate)).getTime();

    if (needsNext && !nextEventData.called) {
      nextEvent({
        variables: { dateFilter: 1, teamId: widgetTeamId! },
        context: { role: 'anonymous' },
      });
    }
  }, [
    nbDaysUpdate,
    liveSettings?.live_id,
    lastEventData.called,
    lastEventData.loading,
    lastEventData.data,
    nextEventData.called,
    nextEvent,
  ]);

  const loadEventData = useQuery(WidgetGetEventDataDocument, {
    variables: {
      eventId: Number(eventId),
      cacheTTL: router.query.noCache ? 0 : undefined,
    },
    skip: !eventId,
    context: {
      role: 'anonymous',
    },
  });

  const liveQuery = useEventLive({
    eventId: Number(eventId),
    generateLive: false,
  });

  const live = liveQuery.data?.lives_live?.[0];
  const event = loadEventData.data?.competitions_event_detail_by_id?.[0];
  const eventTeams = useMemo(() => getEventTeams(event?.teams), [event?.teams]);
  const eventDate = getDateInput(event?.date);

  const loadTeamsSponsorsData = useQuery(WidgetLiveGetTeamsSponsorsDocument, {
    variables: {
      team_id_1: eventTeams[0]?.team_id ?? 0,
      team_id_2: eventTeams[1]?.team_id ?? 0,
      cacheTTL: router.query.noCache ? 0 : undefined,
    },
    skip: !eventTeams[0]?.team_id && !eventTeams[1]?.team_id,
    context: {
      role: 'anonymous',
    },
  });

  const sponsors = loadTeamsSponsorsData.data?.sponsors_team_sponsor;

  if (eventId === null || eventId === undefined) {
    return (
      <>
        <LiveWidgetTitle />
        <Stack
          spacing={4}
          sx={{
            width: '100%',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Container>
            <Typography
              variant="body2"
              component="p"
              sx={{ mt: '6.5rem', mb: '5rem', textAlign: 'center' }}
            >
              {t(T_KEYS.WIDGET_ERROR)}
            </Typography>
          </Container>
        </Stack>
      </>
    );
  }

  return (
    <>
      {event ? (
        <>
          <LiveWidgetTitle status={event.status} />
          <Stack
            spacing={4}
            sx={{
              width: '100%',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                backgroundColor: theme.vars.palette.primary.dark,
                width: '100%',
                position: 'relative',
              }}
            >
              <img
                css={css`
                  position: absolute;
                  object-fit: cover;
                  opacity: 0.15;
                  width: 100%;
                  height: 100%;
                `}
                src={backgroundImagePath(event)}
                alt=""
              />
              <Container>
                <EventHero
                  css={css`
                    width: 100%;
                    padding-top: 2rem;
                    padding-bottom: 2rem;
                  `}
                  event={event}
                  live={live as Types.Lives_Live}
                />
              </Container>
            </Box>
            {!!sponsors?.length && (
              <Container>
                <Stack spacing="1" sx={{ maxWidth: '40rem', m: '0 auto' }}>
                  <Typography
                    variant="body2"
                    sx={{
                      textTransform: 'uppercase',
                      textAlign: 'center',
                      mb: 1,
                    }}
                  >
                    {t(T_KEYS.SPONSORS_LIVE_OFFERED_BY)}
                  </Typography>
                  <SponsorCarousel sponsors={sponsors} />
                </Stack>
              </Container>
            )}

            <Container>
              <Accordion
                square
                disableGutters
                elevation={0}
                sx={{ maxWidth: '40rem', m: '0 auto' }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    borderBottom: '1px solid',
                    borderBottomColor: theme.palette.divider,
                  }}
                >
                  <Typography>{t(T_KEYS.EVENT_INFORMATIONS)}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1} textAlign="center">
                    <Box>
                      {event?.round && (
                        <Typography
                          variant="body1"
                          component="p"
                          sx={{ fontWeight: 'bold' }}
                        >
                          {[
                            event.round.competition_detail?.[0]?.name,
                            event.round.name,
                          ]
                            .filter(Boolean)
                            .join(' - ')}
                        </Typography>
                      )}
                      <Typography variant="body1" component="p">
                        {event.age_category} {event.gender}
                      </Typography>
                    </Box>
                    {eventDate && (
                      <Typography variant="body1" component="p">
                        {format(
                          new Date(eventDate),
                          t(T_KEYS.EVENT_INFO_SLIDE_DATE_FORMAT),
                          {
                            locale: locale === Locale.de_CH ? de : fr,
                          }
                        )}
                      </Typography>
                    )}
                    {event.place && (
                      <Box>
                        <Typography variant="body1" component="p">
                          {[event.place.address, event.place.postal_code]
                            .filter(Boolean)
                            .join(' - ')}{' '}
                          {event.place.name?.toUpperCase()}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </Container>
            <Container>
              <Divider sx={{ width: '100%' }} />
            </Container>
            <Container>
              <Box
                sx={{
                  maxWidth: `40rem`,
                  m: '0 auto',
                }}
              >
                {live && (
                  <WidgetLivePosts
                    live={live as Types.Lives_Live}
                    event={event}
                    widgetReporters={widgetReporters}
                  />
                )}
              </Box>
            </Container>
          </Stack>
        </>
      ) : (
        <>
          <LiveWidgetTitle status={undefined} />
          <Stack
            spacing={4}
            sx={{
              width: '100%',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Skeleton width="100%" height="12rem" variant="rectangular" />
            <Skeleton width="100%" height="15rem" variant="rectangular" />
          </Stack>
        </>
      )}
    </>
  );
};
